import {
  Box,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { flowExecutions } from '../../queries/flow-executions';
import NodeRow from './NodeRow';

interface Props {
  flowId: string;
  execId: string | null;
  expandedNodeIds: Set<string>;
  onToggleNode: (id: string) => void;
}

function ExecutionDetail({
  flowId,
  execId,
  expandedNodeIds,
  onToggleNode,
}: Props) {
  const { data, isLoading, isError } = useQuery(
    flowExecutions.runNodes(flowId, execId),
  );
  if (!execId) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography variant='body2'>
          Select an execution on the left to inspect its nodes.
        </Typography>
      </Box>
    );
  }
  if (isLoading) {
    return (
      <Stack spacing={1} sx={{ p: 1 }}>
        {[0, 1, 2, 3].map(index => (
          <Skeleton key={index} variant='rounded' height={48} />
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <CircularProgress size={18} />
        </Box>
      </Stack>
    );
  }
  if (isError || !data) {
    return (
      <Typography color='error' variant='body2' sx={{ p: 2 }}>
        Failed to load execution detail.
      </Typography>
    );
  }
  if (data.length === 0) {
    return (
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ p: 4, textAlign: 'center' }}
      >
        No node executions recorded for this run.
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      {data.map(node => (
        <NodeRow
          key={node.id}
          node={node}
          expanded={expandedNodeIds.has(node.id)}
          onToggle={onToggleNode}
        />
      ))}
    </Stack>
  );
}

export default ExecutionDetail;
